import { Actions } from '#base/Canvas/Actions.js';
import { elementToUrl, renderToPNGUrl } from '#base/Canvas/Canvas.js';
import { DesignCanvas } from '#base/Canvas/DesignCanvas.js';
import { DesignerDispatch } from '#base/Canvas/Dispatch.js';
import DesignerStore from '#base/Canvas/Store.js';
import {
  DesignerView,
  FacePagination,
  FaceToggle,
  GroupMembersRenderer,
  Header,
} from '#base/Designer/index.js';
import { faceDisplayName } from '#base/Designer/FaceToggle.js';
import RTICLogoIcon from '#base/resources/icons/rtic-logo.svg';
import { GraphicTypeSelect } from '#base/ui/GraphicType.js';
import { Spinner } from '#base/ui/Spinner.js';
import { ErrorBoundary } from '#base/util/error.js';
import { useStateRef, useWait } from '#base/util/hook.js';
import { combineImages, uploadImageAsync } from '#base/util/image.js';
import { version } from '#base/version.js';
import { createAssetLibraryFromServerData, TEXT_PLACEHOLDER, withFee } from '@c2s-npm/plumlib';
import { vectorizeAsync, vectorizeBasicTransparentAsync } from '@c2s-npm/vectorize/vectorize.js';
import classNames from 'classnames';
import { fromJS, List, Map } from 'immutable';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useSwipeable } from 'react-swipeable';
import { groupsGraphicTypeFilter } from '#base/AssetGroup.js';

const createDXF = async (config, graphicType, assetLibrary, elements) => {
  let url = await renderToPNGUrl(config, graphicType, assetLibrary, elements);
  let pngBytes = await (await fetch(url)).arrayBuffer();
  let result = await vectorizeBasicTransparentAsync(pngBytes, 'out.png');
  return result.dxf.buffer;
};

const createPrintablePNG = async (config, graphicType, assetLibrary, elements) => {
  let url = await renderToPNGUrl(config, graphicType, assetLibrary, elements);
  return await (await fetch(url)).arrayBuffer();
};

const requestPreview = async (graphicType, url, config, dxfPromises) => {
  let utf8decoder = new TextDecoder();
  let form = new FormData();
  form.append('itemId', config[0].itemId); // itemId is same for both faces

  const allDxfPromises = Object.keys(dxfPromises).map((key) => {
    return dxfPromises[key].then((s) => {
      var dxf, obj;
      if (graphicType === 'engraved') {
        dxf = utf8decoder.decode(s);
        obj = {
          face: key,
          name: `${key}DXF`,
          dxf: dxf,
        };
      } else {
        dxf = s;
        obj = {
          face: key,
          name: `${key}PNG`,
          dxf: dxf,
        };
      }
      return obj;
    });
  });

  let dxfByFace = {};
  let allPromise = await Promise.all(allDxfPromises);
  allPromise.forEach((o) => {
    dxfByFace[o.face] = o.dxf;
    form.append(o.name, new Blob([o.dxf]), o.name);
  });

  let result = fetch(url, {
    method: 'POST',
    body: form,
  })
    .then((response) => response.formData())
    .then((formData) => {
      let preview = {};
      Object.keys(dxfByFace).forEach((face) => {
        const svgName = `${face}SVG`;

        if (formData.has(svgName)) {
          let svgData = formData.get(svgName);
          var dataUrl, dxfData;
          if (svgData.startsWith('data:')) {
            dataUrl = svgData;
            dxfData = svgData;
          } else {
            dataUrl = 'data:image/svg+xml;utf8,' + encodeURIComponent(formData.get(svgName));
            dxfData = dxfByFace[face];
          }
          preview[face] = {
            elementType: 'preview',
            id: -1,
            dataUrl: dataUrl,
            dxf: dxfData,
            rotation: 0.0,
            scale: 1.0,
            x: 0.0,
            y: 0.0,
          };
        }
      });
      return preview;
    });

  return result;
};

export const Designer = React.forwardRef((props, ref) => {
  return (
    <ErrorBoundary>
      <Provider store={DesignerStore}>
        <DesignerImpl ref={ref} {...props} />
      </Provider>
    </ErrorBoundary>
  );
});

const DesignerImpl = React.forwardRef(
  (
    {
      config,
      graphicType: defaultGraphicType = 'engraved',
      graphicTypes: availableGraphicTypes,
      library,
      uploaded,
      uploadUrl,
      onUpload,
      design,
      itemInfoComponent: ItemInfoComponent,
      getTotalPrice,
      designerHeaderComponent: DesignerHeaderComponent,
      titleComponent: TitleComponent,
      submitText,
      onSubmit,
      onInit,
      onBack,
      previewUrl,
      height,
      debug,
      item,
      withText,
      withGroup,
      withJersey,
      withMonogram,
      withArtwork,
      withUpload,
      wantUploadsDeferred = true,
      assetGroupFilter,
      partnerId,
      contentClassName,
      visible = true,
      showReview,
      mode,
      ...restProps
    },
    forwardRef
  ) => {
    const state = useSelector((s) => s);
    const dispatch = useDispatch();

    const productDetailsAnchor = useRef(null);

    const designerViewRef = useRef();
    const canvasRef = useRef();
    const designerRef = useRef();

    const [graphicType, setGraphicType] = useState(defaultGraphicType);
    const toggleGraphicType = () => setGraphicType(graphicType === 'color' ? 'engraved' : 'color');
    const [previewMessage, setPreviewMessage] = useState(null);
    const [previewObj, setPreviewObj, getPreviewObj] = useStateRef(fromJS({}));
    const [snappy, setSnappy] = useState(true);
    const toggleSnappy = () => setSnappy(!snappy);

    const [ units, setUnits ] = useState('in');

    const nudgeRef = useRef();
    const [nudge, setNudge] = useState(1);
    const handleNudge = () => {
      const newNudge = parseFloat(nudge);
      if (!isNaN(newNudge)) {
        setNudge(newNudge);
      } else {
        setNudge(1);
      }
    };
    const handleEnter = (e) => {
      if (e.code === 'Enter' || e.code === 'Tab') {
        e.preventDefault();
        e.target.blur();
      }
    };

    const handleKeyDown = useCallback(
      (e) => {
        if (mode !== 'pro') return;

        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
          return;
        }

        switch (e.key) {
          case 'n':
            nudgeRef.current.focus();
            e.preventDefault();
            break;
          default:
            break;
        }
      },
      [mode]
    );

    useEffect(() => {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [handleKeyDown]);

    const selectedFace = state.get('selectedFace');
    const faces = state.get('faces').filter((f) => !config || !!config.find((c) => c.face === f.get('face')));
    const faceIndex = faces.findIndex((f) => f.get('face') === selectedFace);
    const nextFace =
      faces.size > 1 && (faceIndex + 1 < faces.size ? faces.get(faceIndex + 1) : faces.get(0)).get('face');
    const prevFace = faces.size > 1 && faces.get(faceIndex - 1).get('face');

    const [view, _setView, getView] = useStateRef('design');
    const isPreview = view?.startsWith('review');
    const previewProcessing = previewObj.get('processing');
    const previewError = previewObj.get('error');
    const previewElements = [previewObj.getIn(['data', selectedFace])].filter((v) => !!v);

    const [_processing, setProcessing] = useState(false);
    const SUBMIT_NONE = 0;
    const SUBMIT_PREP = 1;
    const SUBMIT_READY_TO_SEND = 2;
    const [submitState, setSubmitState] = useState(SUBMIT_NONE);
    const [previewFaces, setPreviewFaces] = useState(null);
    const [exportRender, setExportRender] = useState(false);

    const [zoom, setZoom] = useState(restProps.zoom || 'full');

    const processing = previewProcessing || submitState > SUBMIT_NONE || _processing;
    const canExport = !previewError;

    const hasElements = faces.some((face) => face.get('elements').size > 0);

    useEffect(() => {
      const loadDesign = async () => {
        if (design && design.faces) {
          console.log('Loading design', { design });
          setProcessing(true);
          setPreviewMessage('Loading design...');

          setTimeout(async () => {
            const faces = await Promise.all(
              design.faces.map(async (f) => {
                const elements = await Promise.all(
                  f.elements.map(async (e) => {
                    if (e.elementType === 'file' && !!e.vectorizeMethod && e.vectorizeMethod !== 'Color') {
                      const url = await fetch(e.url)
                        .then((r) => r.arrayBuffer())
                        .then(async (data) => {
                          const option = (await vectorizeAsync(data, 'source')).find(
                            (v) => v.name === e.vectorizeMethod
                          );
                          const svgBase64 = btoa(
                            option.svg.reduce((data, byte) => data + String.fromCharCode(byte), '')
                          );
                          const imageURI = 'data:image/svg+xml;base64,' + svgBase64;
                          return imageURI;
                        })
                        .catch((e) => {
                          console.error('Error vectorizing', e);
                          return e.url;
                        });
                      return Object.assign(e, { url: url || e.url });
                    } else {
                      return e;
                    }
                  })
                );
                return Object.assign(f, { elements });
              })
            );

            const withDataUrls = Object.assign(design, { faces });

            dispatch(Actions.setDesign(withDataUrls));
            setProcessing(false);
          }, 0);
        }
      };
      loadDesign();
    }, [design]);

    useEffect(() => {
      const [assets, unfilteredGroups] = createAssetLibraryFromServerData(library);
      const groupKeys = unfilteredGroups ? Object.keys(unfilteredGroups) : [];
      const groups = {};
      for (const key of groupKeys) {
        const filteredGroups = unfilteredGroups[key].filter(groupsGraphicTypeFilter(graphicType));

        filteredGroups.forEach((group) => {
          group.groups = [...group.groups.filter((g) => filteredGroups.findIndex((fg) => fg.id === g) >= 0)];
        });

        groups[key] = filteredGroups;
      }

      dispatch(Actions.setAssetLibrary({ assets, groups: unfilteredGroups }));
    }, [library, graphicType]);
    const { assets: assetLibrary, groups: assetGroups } = state.get('assetLibrary');
    const allAssets = library?.assets || [];

    const elements = faces.find((f) => f.get('face') === selectedFace)?.get?.('elements') || new List();

    const allFacesElements =
      faces
        .toJS()
        ?.flatMap((f) =>
          f.elements.map((e) => ({ ...allAssets.find((a) => a.id === e.assetId), ...e, face: f.face }))
        ) || [];

    const selectedElements = state.get('selectedElements');

    const facesWithElements = useMemo(() => faces.filter((f) => f.get('elements').size > 0), [faces]);
    const restrictionInfo = useMemo(() => {
      if (!allAssets || !elements) return {};

      const faceElements = elements
        .map((e) => allAssets.find((a) => a.id === e.get('assetId')))
        .filter((e) => !!e);
      const nonFaceElements = faces
        .filter((f) => f.get('face') !== selectedFace)
        .flatMap((f) => f.get('elements').map((e) => allAssets.find((a) => a.id === e.get('assetId'))))
        .filter((e) => !!e);

      const selected = (selectedElements || [])
        .map((id) => elements.find((e) => e.get('id') === id))
        .map((e) => faceElements.find((f) => f?.id === e.get('assetId')))
        .filter((e) => !!e);

      const faceHasPremium = faceElements.some((a) => !!a?.feeId);
      const nonFaceHasPremium = nonFaceElements.some((a) => !!a?.feeId);
      const itemHasPremium = faceHasPremium || nonFaceHasPremium;

      const faceHasOtherElements = faceElements.size > 1;
      const premiumSelected = selected.some((e) => !!e?.feeId);

      const nonFaceRestrictionType = nonFaceElements.reduce((a, e) => {
        if (e?.restrictionType === 'per_item' || e?.restrictionType === 'per_item_only') {
          a = e.restrictionType;
        } else if (e?.restrictionType === 'per_side' && !a) {
          a = e.restrictionType;
        }
        return a;
      }, '');

      const faceHasPerItemOnly = faceElements.toJS().some((e) => e?.restrictionType === 'per_item_only');
      const nonFaceHasPerItemOnly = nonFaceElements
        .toJS()
        .some((e) => e?.restrictionType === 'per_item_only');
      const hasPerItemOnly = nonFaceHasPerItemOnly || faceHasPerItemOnly;

      const canUsePremium =
        !['per_item', 'per_item_only'].includes(nonFaceRestrictionType) &&
        !faceHasOtherElements &&
        (premiumSelected || faceElements.size === 0);
      const limit = ['per_item', 'per_item_only'].includes(nonFaceRestrictionType)
        ? 'all'
        : nonFaceRestrictionType === 'per_side'
          ? 'per_side'
          : null;

      const hasPremium = itemHasPremium ? (faceHasPremium ? 'face' : 'item') : null;

      return {
        limit,
        restrictPremium: !canUsePremium,
        faceHasPremium,
        nonFaceHasPremium,
        itemHasPremium,
        hasPremium,
        hasPerItemOnly,
        faceHasPerItemOnly,
        nonFaceHasPerItemOnly,
      };
    }, [allAssets, faces, elements, selectedFace, selectedElements]);
    const { restrictPremium, faceHasPremium, hasPremium, hasPerItemOnly } = restrictionInfo;

    const [toolView, _setToolView] = useState();
    // const [view, _setView] = useState('design');

    const [_waiting, setWaitFor] = useWait();

    const isTablet = useMediaQuery({ maxWidth: 1023 });

    const swipeHandler = useSwipeable({
      onSwipedRight: () => {
        if ((config && config.length <= 1) || !!toolView) return;
        const currentIndex = sortedConfig.findIndex((c) => c.face === selectedFace);
        const nextIndex = currentIndex === 0 ? sortedConfig.length - 1 : currentIndex - 1;
        dispatch(Actions.addFace(sortedConfig[nextIndex]));
      },
      onSwipedLeft: () => {
        if ((config && config.length <= 1) || !!toolView) return;
        const currentIndex = sortedConfig.findIndex((c) => c.face === selectedFace);
        const nextIndex = currentIndex === sortedConfig.length - 1 ? 0 : currentIndex + 1;
        dispatch(Actions.addFace(sortedConfig[nextIndex]));
      },
      delta: 40,
      preventScrollOnSwipe: false,
      trackTouch: true,
      trackMouse: false,
      rotationAngle: 0,
      swipeDuration: Infinity,
      touchEventOptions: { passive: true },
    });

    const setView = (vue) => {
      if (vue !== view) {
        _setView(vue);

        visible && isTablet && window.scrollTo(window.scrollX, 0);
      }
    };

    const setToolView = (vue) => {
      if (vue !== toolView) {
        console.log('setToolView', vue)
        _setToolView(vue);

        visible && isTablet && window.scrollTo(window.scrollX, 0);
      }
    };

    const getPreview = () => {
      dispatch(Actions.selectNone());
      setPreviewMessage('Generating Preview');
      setPreviewObj(fromJS({ processing: true }));

      if (graphicType === 'color' && !exportRender) {
        const colorPromises = config
          .map((config) => {
            const { face } = config;
            const elements = faces.find((f) => f.get('face') === face)?.get?.('elements') || new Map();
            return elements.size > 0 ? { face, config, elements } : null;
          })
          .filter((v) => !!v)
          .map(({ face, config, elements }) => {
            return renderToPNGUrl(config, graphicType, assetLibrary, elements, dispatch, 100).then(
              (dataUrl) => ({
                face,
                dataUrl,
              })
            );
          });

        Promise.all(colorPromises).then((values) => {
          const data = values.reduce((a, { face, dataUrl }) => {
            a[face] = {
              elementType: 'preview',
              id: -1,
              dataUrl,
              rotation: 0.0,
              scale: 1.0,
              x: 0.0,
              y: 0.0,
            };
            return a;
          }, {});
          setPreviewObj(fromJS({ processing: false, data, complete: true }));
          setPreviewMessage(null);
        });

        return;
      }

      const promises = config.reduce((a, faceConfig) => {
        const elements = faces.find((f) => f.get('face') === faceConfig.face)?.get?.('elements') || new Map();
        if (elements.size > 0) {
          if (graphicType === 'engraved') {
            a[faceConfig.face] = createDXF(faceConfig, graphicType, assetLibrary, elements);
          } else {
            a[faceConfig.face] = createPrintablePNG(faceConfig, graphicType, assetLibrary, elements);
          }
        }
        return a;
      }, {});

      requestPreview(graphicType, previewUrl, config, promises).then(
        (data) => {
          setPreviewObj(fromJS({ processing: false, data, complete: true }));
          setPreviewMessage(null);
        },
        (error) => {
          console.error(error);
          setPreviewObj(fromJS({ processing: false, error }));
          setPreviewMessage('Could not get preview');
        }
      );
    };

    // If we delete an item, we need to update the preview. But the delete is
    // dispatched, so we need to wait until we do the redraw, when the object
    // will be deleted, before we can ask for the preview again.
    useEffect(() => {
      if (!hasElements && !!view.match(/^review/)) {
        setView('design');
      } else if (view === 'review_updatepreview') {
        setView('review');
      } else if (view === 'review') {
        var removedObj = false;
        faces.forEach((face) => {
          face.get('elements').forEach((element) => {
            if (element.get('data') === TEXT_PLACEHOLDER) {
              removeElementHandler(face.get('face'), element.get('id'));
              removedObj = true;
            }
          });
        });
        if (removedObj) {
          // The delete is not reflected in the data yet, so we cannot request
          // the preview until it is.
          setView('review_updatepreview');
        } else {
          getPreview();
        }
      } else {
        setPreviewMessage(null);
      }
    }, [view, hasElements]);

    useEffect(() => {
      if (!selectedFace && !faces.size && config?.[0]) {
        dispatch(Actions.addFace(config[0]));
      }
    }, [config, selectedFace, faces.size]);

    /*
    // causes infinite loop on export
    useEffect(() => {
      if (view === 'review' && elements.size === 0 && facesWithElements.size > 0) {
        dispatch(Actions.selectFace(facesWithElements.getIn([0,'face'])));
      }
    }, [view, elements, facesWithElements ]);
    */

    useEffect(() => {
      config?.[0] && dispatch(Actions.addFace(config[0]));
    }, [config]);

    const graphicTypes = useMemo(() => {
      return (
        availableGraphicTypes ||
        Object.values(
          (config || []).reduce(
            (a, c) =>
              c.graphicType.reduce((a, g) => {
                a[g] = g;
                return a;
              }, a),
            {}
          )
        )
      );
    }, [availableGraphicTypes, config]);

    useEffect(() => {
      if (!graphicTypes.includes(graphicType)) {
        setGraphicType(graphicTypes[0]);
      }
    }, [graphicType, graphicTypes]);

    const initSubmit = async () => {
      if (canExport) {
        setPreviewMessage('Generating design files...');
        setSubmitState(SUBMIT_PREP);

        let facesWithElementsJS = facesWithElements.toJS();
        let uploadPromises = [];
        for (let face of facesWithElementsJS) {
          for (let e of face.elements) {
            if (e.elementType === 'uploaded' || e.elementType === 'file') {
              if (typeof e.fileId === 'string') {
                let fileId = e.fileId;
                uploadPromises.push(
                  new Promise(async (resolve, reject) => {
                    if (!e.deferredDataUrl) {
                      reject("no 'deferredDataUrl' field");
                    }
                    let blob = await (await fetch(e.deferredDataUrl)).blob();
                    uploadImageAsync(uploadUrl, new File([blob], e.filename, { type: blob.type }))
                      .then((data) => {
                        const newFileId = data[0].id;
                        for (let face of facesWithElementsJS) {
                          for (let e of face.elements) {
                            if (e.hasOwnProperty('fileId') && e.fileId === fileId) {
                              e.fileId = newFileId;
                              delete e.deferredDataUrl;
                              dispatch(Actions.setElement(face.face, e.id, fromJS(e)));
                              // continue iterating, in case the same image was used multiple times
                            }
                          }
                        }
                        resolve(newFileId);
                      })
                      .catch((reason) => {
                        console.log(
                          `[debug] failed to upload custom image file ${fileId}: ${reason.status} ${reason.statusText}`
                        );
                        reject(`${reason.status} ${reason.statusText}`);
                      });
                  })
                );
              }
            }
          }
        }
        try {
          await Promise.all(uploadPromises);
        } catch (ex) {
          alert(`Internal error: failed to upload custom files.\n${ex}\n\nPlease try again.`);
          setSubmitState(SUBMIT_NONE);
          return;
        }

        setPreviewFaces({});
        //dispatch(Actions.selectFace(facesWithElements.get(0).get('face')));
        dispatch(Actions.selectFace(config[0].face));
        setSubmitState(SUBMIT_READY_TO_SEND);
      } else {
        console.warn('Missing necessary files to export.');
      }
    };

    const finishSubmit = async () => {
      setPreviewMessage(null);
      setSubmitState(SUBMIT_NONE);

      // Paranoia check that deferredDataUrl doesn't get submitted
      let facesWithElementsJS = facesWithElements.toJS();
      for (let face of facesWithElementsJS) {
        for (let e of face.elements) {
          if (e.hasOwnProperty('deferredDataUrl')) {
            alert('Internal error: deferredDataUrl should have been removed');
            return;
          }
        }
      }

      const preview = previewObj
        .get('data', new Map())
        .mapEntries(([k, v]) => [k, v.get('dataUrl')])
        .toJS();
      const render = previewObj
        .get('data', new Map())
        .mapEntries(([k, v]) => [k, v.get('dxf')])
        .toJS();

      const printType = facesWithElements.size > 1 ? 'double' : 'single';

      const combined = await combineImages(config.map(({ face }) => previewFaces[face]));

      onSubmit &&
        onSubmit({
          version,
          id: design?.id,
          itemId: config[0].itemId,
          partnerId,
          graphicType,
          printType,
          preview: combined,
          faces: facesWithElementsJS.map((face) =>
            Object.assign(face, {
              preview: preview[face.face],
              render: render[face.face],
              productPreview: previewFaces[face.face],
            })
          ),
        });
    };

    useEffect(() => {
      const submit = async () => {
        if (submitState === SUBMIT_READY_TO_SEND) {
          if (!previewFaces[selectedFace]) {
            const { face, data: preview } = await designerRef.current.export();
            const newFaces = Object.assign({}, previewFaces, { [face]: preview });
            setPreviewFaces(newFaces);
          } else {
            if (config.every(({ face }) => !!previewFaces[face])) {
              // if (facesWithElements.every((f) => !!previewFaces[f.get('face')])) {
              finishSubmit();
            } else {
              const index = config.findIndex(({ face }) => face === selectedFace);
              const next = config[index + 1]?.face;
              //const index = facesWithElements.findIndex((f) => f.get('face') === selectedFace);
              //const next = facesWithElements.getIn([index + 1, 'face']);
              if (next) {
                dispatch(Actions.selectFace(next));
              }
            }
          }
        }
      };
      submit();
    }, [config, submitState, selectedFace, nextFace, facesWithElements, previewFaces]);

    // clear out default group data if the user left any
    useEffect(() => {
      if (!toolView) {
        faces.forEach((f) => {
          f.get('elements').forEach((e) => {
            if (e.get('isGroup')) {
              const oldData = e.get('data');
              const newData = oldData && oldData.filter((d) => !d.get('isDefault'));
              if (!newData || newData.size === 0) {
                dispatch(Actions.removeElement(f.get('face'), e.get('id')));
              } else if (newData.size !== oldData.size) {
                dispatch(Actions.setElement(f.get('face'), e.get('id'), e.set('data', newData)));
              }
            }
          });
        });
      }
    }, [toolView, faces]);

    const [init, setInit] = useState(false);
    useEffect(() => {
      if (config && library && design && facesWithElements.size > 0 && !init) {
        setInit(true);
      }
    }, [init, config, library, design, facesWithElements]);
    useEffect(() => {
      if (init && onInit) {
        onInit();
      }
    }, [init]);

    const submit = (withRender = false) => {
      setExportRender(withRender);
      setWaitFor(() => getView() === 'review' && getPreviewObj().get('complete'), initSubmit);
      setView('review');
    };

    React.useImperativeHandle(
      forwardRef,
      () => ({
        submit,
        selectFace: (face) => {
          const faceConfig = config.find((c) => c.face === face);
          faceConfig && dispatch(Actions.addFace(faceConfig));
        },
        setView: (view) => {
          setView(view);
        },
        getDesign: () => {
          return state.get('faces').toJS();
        },
        uploadImageAsync: (file) => {
          return uploadImageAsync(file);
        },
      }),
      [submit, dispatch, config, state]
    );

    const uploadFee = useMemo(() => {
      if (!config || !selectedFace) return null;

      const miyoFeeItem = config
        ?.find?.((c) => c.face === selectedFace)
        ?.miyoFees?.find?.((f) => f.graphicType === graphicType && f.feeId === 19675);
      const uploadFeeRaw = miyoFeeItem?.rate;
      const usualRateRaw = miyoFeeItem?.usualRate || 0;

      if (!uploadFeeRaw) return null;

      const rate = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(uploadFeeRaw);

      const usualRate =
        usualRateRaw > 0
          ? new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(usualRateRaw)
          : null;

      return {
        rate,
        usualRate,
      };
    }, [config, selectedFace, graphicType]);

    useEffect(() => {
      if (hasPerItemOnly && library) {
        const perItemOnlyFace = allFacesElements.find((e) => e.restrictionType === 'per_item_only')?.face;

        if (perItemOnlyFace !== selectedFace) {
          dispatch(Actions.selectFace(perItemOnlyFace));
        }
      }
    }, [hasPerItemOnly, selectedFace, allFacesElements]);

    const showGroupsRenderer = useMemo(() => {
      if (!withGroup) return false;

      const _elements = (elements && !!elements.toJS && elements.toJS()) || [];

      const group = _elements.find((element) => element.isGroup);

      if (!group || !group.data || group.data.length < 1) return false;

      return true;
    }, [withGroup, elements]);

    // config defines what is available for the design
    // design defines what the user has done
    if (!config || !library) return null;

    // Back face can come before the front face in the config, but that
    // looks awkward in the UI.
    const sortedConfig = config.toSorted((x, y) => {
      if (x.face === 'front' || x.face === 'logo') {
        return -1;
      }
      if (y.face === 'front' || y.face === 'logo') {
        return 1;
      }
      return x.face < y.face;
    });

    const facesForPricing = faces
      .filter((f) => f.get('elements').size > 0)
      .map((f) => ({
        key: f.get('face'),
        elements: (f.get('elements') ?? [])
          .map((e) => {
            const element = e.toJS();
            if (element.isGroup && Array.isArray(element.data)) {
              element.data = element.data.map((d) => ({
                ...d,
                url: elementToUrl(Object.assign({}, element, d, { isGroup: false }), assetLibrary),
              }));
            }
            return {
              ...element,
              url: elementToUrl(element, assetLibrary),
              meta: f.getIn(['meta', `${e.get('id')}`]),
            };
          })
          .map((e) => {
            const asset = allAssets.find((a) => a.id === e.assetId);
            return withFee(library, asset, e);
          }),
      }))
      .toJS();

    const printType = facesForPricing.length > 1 ? 'double' : 'single';

    // Can't use faceIndex because order of faces in 'config' may not be the
    // same as in 'faces'.
    const faceConfig = config.find((c) => c.face === selectedFace) || config[0];

    const _onDeleteItem = (faceName, elementId) => {
      removeElementHandler(faceName, elementId);
      setView('review_updatepreview'); // to request preview again
    };

    const selectElementHandler = (faceName, elementId) => {
      dispatch(Actions.selectElement(faceName, elementId));
    };

    const removeElementHandler = (faceName, elementId) => {
      dispatch(Actions.removeElement(faceName, elementId));
    };

    const setElementHandler = (faceName, elementId, data) => {
      dispatch(Actions.setElement(faceName, elementId, data));
    };

    const setActiveGroupHandler = (index) => {
      dispatch(Actions.setActiveGroup(index));
    };

    const _onAddPersonalization = (faceObj) => {
      const face = faceObj.get('face');
      dispatch(Actions.addFace(config.find((c) => c.face === face)));
      setView('design');
    };

    const addFaceHandler = (config) => {
      dispatch(Actions.addFace(config));
    };

    const onClickHeader = () => {
      switch (view) {
        case 'design':
          if (toolView) {
            designerViewRef.current.undo();

            if (toolView === 'uploaded') {
              setToolView('upload');
            }
          } else if (onBack) onBack(item);
          else if (item.url) window.location.href = item.url;
          else console.log('unknown product url');
          break;

        case 'terms-and-conditions':
          setView('review');
          break;

        case 'update-variant':
          // TODO this is going to lose the user design...
          window.location.reload();
          break;

        case 'personalization':
        case 'review':
        case 'group':
          setView('design');
          break;

        default:
          console.log('unknown view event ', view);
          break;
      }
    };

    const itemInfoProps = {
      faces: facesForPricing,
      config,
      graphicType,
      printType,
      view,
      setView,
      elements: faces.reduce((a, f) => a.concat(f.get('elements').toJS()), []),
      hasElements,
      processing,
      canExport,
      action: design?.id || design?.copyId ? 'update' : 'add',
      uploaded,
      submit: initSubmit,
      addFace: addFaceHandler,
      selectElement: selectElementHandler,
      removeElement: removeElementHandler,
      setElement: setElementHandler,
      setActiveGroup: setActiveGroupHandler,
    };

    const shouldShowFacePagination =
      (!isTablet || (!toolView && isTablet)) && !hasPerItemOnly && !showGroupsRenderer;

    return (
      <DesignerDispatch.Provider value={dispatch}>
        <style jsx='true'>{`
          iframe[title='Button to launch messaging window'] {
            display: none !important;
          }
        `}</style>

        <div style={visible ? {} : { visibility: 'hidden', height: 0, overflow: 'hidden' }}>
          {!toolView && !['update-variant'].includes(view) && (
            <DesignerHeaderComponent onMoreClick={() => setView('update-variant')} />
          )}
          {(!!toolView || ['update-variant'].includes(view)) && isTablet && (
            <Header
              {...{
                view,
                toolView,
                item,
                onClick: onClickHeader,
                title: getHeaderTitle({ toolView, uploadFee, view }),
              }}
            />
          )}

          <div
            ref={productDetailsAnchor}
            className={classNames('t-mt-10 md:t-pt-10 lg:t-mt-0', contentClassName)}>
            <div
              className={classNames(
                't-flex t-flex-col t-text-[13px] md:t-mb-0 lg:t-mb-20 lg:t-h-auto lg:t-flex-row lg:t-items-stretch lg:t-gap-4',
                {
                  't-gap-2 lg:t-gap-4': (isTablet && !toolView) || zoom === 'design',
                }
              )}
              style={{ height }}>
              <div
                {...swipeHandler}
                className={classNames('t-relative t-flex t-w-full t-flex-col t-items-stretch', {
                  'lg:t-max-w-[calc(100%-456px)]': visible, // added this condition to make sure the width is not set to 0 when the component is not visible which breaks partner design export
                })}>
                {!toolView && config?.length > 1 && !hasPerItemOnly && (
                  <div
                    className='t-absolute t-right-2 t-top-2 t-z-[2] t-flex t-flex-col t-items-center t-justify-center t-gap-1 t-rounded-lg t-bg-white t-p-2 t-shadow-[0px_3px_15px_#00000030] md:t-hidden'
                    onClick={() => {
                      if ((config && config.length <= 1) || !!toolView) return;
                      const currentIndex = sortedConfig.findIndex((c) => c.face === selectedFace);
                      const nextIndex = currentIndex === 0 ? sortedConfig.length - 1 : currentIndex - 1;
                      dispatch(Actions.addFace(sortedConfig[nextIndex]));
                    }}>
                    <img
                      src={RTICLogoIcon}
                      className={classNames('t-h-full t-max-h-[22px] t-max-w-full', {
                        't-hidden': selectedFace !== 'front' && selectedFace !== 'logo',
                      })}
                    />
                    <p className='t-mb-0 t-text-[11px] t-font-bold'>{faceDisplayName(selectedFace)}</p>
                  </div>
                )}

                {processing && (
                  <div className='custom-spinner-wrapper'>
                    <div className='t-inline-block text-center'>
                      <Spinner size='lg' />
                      {previewMessage && (
                        <div className='t-mt-3 t-rounded t-bg-white t-p-3 t-opacity-70 t-shadow-md'>
                          {previewMessage}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {isPreview && previewError && (
                  <div className='custom-spinner-wrapper'>
                    <div className='t-inline-block t-rounded t-bg-white t-p-3 t-shadow-md text-center'>
                      <i className='fa fa-exclamation-triangle fa-2x text-muted t-opacity-30' />
                      <div className='text-danger t-mb-3'>{previewMessage || 'Error generating preview'}</div>
                      <a onClick={getPreview}>
                        <i
                          className='fa fa-refresh fa-2x t-cursor-pointer hover:t-animate-spin'
                          title='Try Again'
                        />
                      </a>
                    </div>
                  </div>
                )}
                <div
                  ref={canvasRef}
                  className='t-h-full t-w-full t-grow t-overflow-hidden'
                  style={{ position: 'relative' }}>
                  {faceConfig && (
                    <DesignCanvas
                      ref={designerRef}
                      config={faceConfig}
                      selectedFace={selectedFace}
                      elements={isPreview ? previewElements : elements}
                      selected={isPreview ? [] : selectedElements}
                      assetLibrary={assetLibrary}
                      canEdit={view === 'design'}
                      message={false && isPreview && previewMessage}
                      zoom={isTablet && !!toolView ? 'design' : zoom}
                      graphicType={graphicType}
                      snappy={snappy}
                      mode={mode}
                      units={units}
                      nudge={nudge}
                      debug={debug}
                      isPreview={isPreview}
                    />
                  )}
                  {(debug || mode === 'pro') && (
                    <div style={{ position: 'absolute', top: 10, left: 10 }}>
                      <a
                        onClick={toggleGraphicType}
                        title='Toggle Graphic Type'
                        style={{
                          backgroundColor: graphicType === 'color' ? '#3cf' : '#999',
                          display: 'inline-block',
                          color: '#fff',
                          borderRadius: '30px',
                          width: '30px',
                          height: '30px',
                          textAlign: 'center',
                          lineHeight: '30px',
                          cursor: 'pointer',
                          margin: '5px',
                        }}>
                        <i className='fa fa-paint-brush' />
                      </a>
                      <br />
                      <a
                        onClick={() => setZoom('design')}
                        style={{
                          backgroundColor: zoom === 'design' ? '#3cf' : '#999',
                          display: 'inline-block',
                          color: '#fff',
                          borderRadius: '30px',
                          fontSize: '30px',
                          width: '30px',
                          height: '30px',
                          textAlign: 'center',
                          lineHeight: '30px',
                          cursor: 'pointer',
                          margin: '5px',
                        }}>
                        +
                      </a>
                      <br />
                      <a
                        onClick={() => setZoom('full')}
                        style={{
                          backgroundColor: zoom === 'full' ? '#3cf' : '#999',
                          display: 'inline-block',
                          color: '#fff',
                          borderRadius: '30px',
                          fontSize: '30px',
                          width: '30px',
                          height: '30px',
                          textAlign: 'center',
                          lineHeight: '30px',
                          cursor: 'pointer',
                          margin: '5px',
                        }}>
                        -
                      </a>
                      <br />
                      <a
                        onClick={toggleSnappy}
                        title='Toggle snap to grid'
                        style={{
                          backgroundColor: snappy ? '#3cf' : '#999',
                          display: 'inline-block',
                          color: '#fff',
                          borderRadius: '30px',
                          width: '30px',
                          height: '30px',
                          textAlign: 'center',
                          lineHeight: '30px',
                          cursor: 'pointer',
                          margin: '5px',
                        }}>
                        <i className='fa fa-thumb-tack' />
                      </a>
                      <br />
                      <div className='text-muted t-my-2 t-text-center'>
                        <span>Nudge</span>
                        <br />
                        <input
                          ref={nudgeRef}
                          type='text'
                          className='t-mx-auto t-w-[30px] t-text-center'
                          value={nudge}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => setNudge(e.target.value)}
                          onBlur={handleNudge}
                          onKeyDown={handleEnter}
                        />
                        <div>
                          {['mm','in'].map(u => <div key={u} className='t-text-left'><label>
                            <input type='radio' name='units' value={u} checked={u === units} onChange={() => setUnits(u)}/>
                            {' '}
                            {u}
                          </label></div>)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* <FaceToggle
                  className='t-m-1 t-text-center md:t-m-2'
                  {...{ config: sortedConfig, selectedFace, size: 'sm' }}
                /> */}

                {shouldShowFacePagination && (
                  <FacePagination
                    {...{ config: sortedConfig, selectedFace }}
                    onPrevClick={() => dispatch(Actions.addFace(config.find((c) => c.face === prevFace)))}
                    onNextClick={() => dispatch(Actions.addFace(config.find((c) => c.face === nextFace)))}
                  />
                )}
                {showGroupsRenderer && (
                  <GroupMembersRenderer
                    {...{ elements, selectedFace }}
                    className='t-hidden md:t-mt-5 md:t-block'
                  />
                )}
                {TitleComponent && isTablet && (!toolView || zoom === 'design') && (
                  <div className='t-mt-3'>
                    <TitleComponent {...{ item, config, graphicType, selectedFace }} />
                  </div>
                )}
              </div>
              <div
                id='designer-details'
                className='t-w-[calc(100%+32px)] -t-mx-4 t-flex t-h-full t-bg-white t-py-5 t-shadow-[0_-5px_20px_#00000020] lg:t-mx-auto lg:t-mr-0 lg:t-block lg:t-w-full lg:t-max-w-[440px] lg:t-py-0 lg:t-pb-0 lg:t-shadow-none'>
                {!isTablet && (
                  <Header
                    {...{
                      view,
                      toolView,
                      item,
                      onClick: onClickHeader,
                      title: getHeaderTitle({ toolView, uploadFee, view }),
                    }}
                  />
                )}

                <div className='t-w-full t-bg-white t-px-4 lg:t-rounded-xl lg:t-border lg:t-border-neutrals-light-grey lg:t-px-10 lg:t-py-8'>
                  {view === 'design' && !toolView && (
                    <div className='t-flex t-w-full t-flex-col t-gap-2'>
                      {TitleComponent && !isTablet && (
                        <TitleComponent {...{ item, config, graphicType, selectedFace }} />
                      )}

                      {!hasPerItemOnly && (
                        <div className='t-order-2 t-hidden lg:t-block'>
                          <FaceToggle
                            className='t-my-1 t-text-center md:t-my-2'
                            {...{ config: sortedConfig, selectedFace }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {view === 'design' && !toolView && graphicTypes.length > 1 && (
                    <GraphicTypeSelect value={graphicType} onSelect={setGraphicType} />
                  )}

                  {['personalization', 'review', 'terms-and-conditions', 'update-variant'].includes(view) && (
                    <>{ItemInfoComponent && <ItemInfoComponent {...itemInfoProps} />}</>
                  )}

                  {view === 'design' && (
                    <DesignerView
                      ref={designerViewRef}
                      {...{
                        assetGroups,
                        typeId: item.typeId,
                        assetGroupFilter,
                        assetLibrary,
                        config: sortedConfig,
                        allFacesElements,
                        elements,
                        hasElements,
                        hasPremium,
                        hasPerItemOnly,
                        faceHasPremium,
                        restrictPremium,
                        restrictionInfo,
                        ItemInfoComponent,
                        getTotalPrice,
                        itemInfoProps,
                        selectedElements,
                        selectedFace,
                        setToolView,
                        setView,
                        toolView,
                        uploaded,
                        uploadUrl,
                        onUpload,
                        withText,
                        withGroup,
                        withJersey,
                        withMonogram,
                        withArtwork,
                        withUpload,
                        wantUploadsDeferred,
                        graphicType,
                        showReview,
                        uploadFee,
                        debug,
                        mode,
                        units,
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
            {mode === 'pro' && (
              <div
                style={{ border: 'solid 1px #ccc', marginTop: '1em', marginBottom: '1em', padding: '1em' }}>
                <strong>Shortcuts</strong>
                <ul>
                  <li>n - nudge</li>
                </ul>
              </div>
            )}
            {debug && (
              <div style={{ border: 'solid 1px #ccc', marginTop: '1em', padding: '1em' }}>
                DESIGN DEBUG: <pre>{prettyDesignJson(state.get('faces').toJS())}</pre>
              </div>
            )}
          </div>
        </div>
      </DesignerDispatch.Provider>
    );
  }
);

const prettyDesignJson = (faces) => {
  const withoutDataUrls = faces.map((f) => {
    return Object.assign({}, f, {
      elements: f.elements.map((e) => {
        if (e.url && e.url.startsWith('data:')) {
          return Object.assign({}, e, { url: '[[data]]' });
        } else {
          return e;
        }
      }),
    });
  });

  return JSON.stringify(withoutDataUrls, null, 2);
};

Designer.propTypes = {
  config: PropTypes.any.isRequired,
  library: PropTypes.any,
  assetGroupFilter: PropTypes.any,
  partnerId: PropTypes.number,
  uploaded: PropTypes.any,
  design: PropTypes.any,
  uploadUrl: PropTypes.string.isRequired,
  previewUrl: PropTypes.string.isRequired,
  height: PropTypes.any,
  withText: PropTypes.bool,
  withGroup: PropTypes.bool,
  withJersey: PropTypes.bool,
  withMonogram: PropTypes.bool,
  withArtwork: PropTypes.bool,
  withUpload: PropTypes.bool,
  debug: PropTypes.bool,
  itemInfoComponent: PropTypes.any,
  getTotalPrice: PropTypes.func,
  headerComponent: PropTypes.any,
  titleComponent: PropTypes.any,
  onSubmit: PropTypes.func,
  onInit: PropTypes.func,
  zoom: PropTypes.string,
};
Designer.defaultProps = {
  zoom: 'full',
  withText: true,
  withMonogram: true,
  withJersey: false,
  withArtwork: true,
  withUpload: true,

  itemInfoComponent: () => null,
  designerHeaderComponent: () => null,
  titleComponent: () => null,
  getTotalPrice: () => null,
};

export const Icon = ({ url }) => {
  return (
    <span>
      <img src={url} className='tool-icon' />
    </span>
  );
};

const getHeaderTitle = ({ toolView, uploadFee, view }) => {
  if (view === 'update-variant') return 'Update Product';
  if (view === 'review') return 'Review your Design';

  if (toolView === 'group') return 'Group Order';

  if ((toolView || '').match(/^group./)) return 'Name Template';
  if (toolView === 'font') return 'Text';
  if (toolView === 'monogram') return 'Monogram';
  if (toolView === 'artwork') return 'Artwork';
  if (toolView === 'jersey') return 'Jersey';
  if (toolView === 'uploaded')
    return (
      <span className='t-inline-flex t-items-center t-gap-2'>
        <span>Your Designs</span>
        {uploadFee?.rate && (
          <span
            className={classNames({
              't-text-brand-black': !uploadFee?.usualRate,
              't-text-onsale t-font-bold': !!uploadFee?.usualRate,
            })}>
            {uploadFee?.rate}{' '}
            {uploadFee?.usualRate && (
              <span className='t-text-brand-black t-font-normal t-line-through'>{uploadFee.usualRate}</span>
            )}
          </span>
        )}
      </span>
    );
  if (toolView === 'upload')
    return (
      <span className='t-inline-flex t-items-center t-gap-2'>
        <span>Upload your Design</span>
        {uploadFee?.rate && (
          <span
            className={classNames({
              't-text-brand-black': !uploadFee?.usualRate,
              't-text-onsale t-font-bold': !!uploadFee?.usualRate,
            })}>
            {uploadFee?.rate}{' '}
            {uploadFee?.usualRate && (
              <span className='t-text-brand-black t-font-normal t-line-through'>{uploadFee.usualRate}</span>
            )}
          </span>
        )}
      </span>
    );

  return 'Back to MyRTIC';
};
